package com.sddamico.draftmachine.components.sections

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.components.layouts.MobileFrame
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.backgroundImageRaw
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.dmPanel
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.navigation.Cog
import com.sddamico.draftmachine.ds.roleH3
import com.sddamico.draftmachine.ds.roleSmall
import com.sddamico.draftmachine.ds.brand.Logo
import com.sddamico.draftmachine.ds.brand.LogoVariant
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

private const val HOW_IT_RUNS_ID = "how-it-runs"

/**
 * Frame 1a — the unauthenticated home "pitch". A single mobile-first column with the ambient
 * cog motif, a hero, three feature panels, a "how it runs" ladder and a closing CTA. Every
 * "Log in" affordance calls [onLogin]; "See how it runs" scrolls down to the ladder.
 */
@Composable
fun PitchSection(modifier: Modifier = Modifier, onLogin: () -> Unit) {
  MobileFrame(
    modifier = Modifier.position(Position.Relative).styleModifier { property("overflow", "hidden") }
      .then(modifier),
  ) {
    Cog(
      modifier = Modifier.position(Position.Absolute).styleModifier {
        property("top", "-70px"); property("right", "-80px")
      },
      size = 220.px, color = Color.rgba(53, 66, 78, 0.5f), speed = "60s",
    )
    Cog(
      modifier = Modifier.position(Position.Absolute).styleModifier {
        property("top", "120px"); property("right", "96px")
      },
      size = 130.px, color = Color.rgba(59, 88, 103, 0.4f), speed = "44s", reverse = true,
    )

    // Header — wordmark + ghost "Log in".
    Div(
      attrs = layer().displayFlex().alignItems(AlignItems.Center).gap(10.px)
        .height(56.px).padding(leftRight = 16.px).toAttrs()
    ) {
      Logo(variant = LogoVariant.Full, size = 22.0)
      Div(attrs = Modifier.flex(1).toAttrs()) {}
      Button("Log in", variant = ButtonVariant.Ghost, size = ButtonSize.Sm, onClick = onLogin)
    }

    // Hero.
    Div(attrs = layer().padding(top = 28.px, leftRight = 20.px, bottom = 24.px).toAttrs()) {
      Stamp("Async drafts · any sport · any league", DmColor.brass400, bottom = 12.px)
      SpanText(
        "Your draft, on your time.",
        Modifier.fontFamily(*DmType.display).fontSize(36.px).lineHeight(1.04)
          .color(DmColor.text).displayBlock().margin(bottom = 14.px),
      )
      SpanText(
        "The Draft Machine runs fantasy drafts for the leagues the big platforms won't " +
          "touch — over email, at everyone's own pace. No draft night required.",
        Modifier.fontFamily(*DmType.body).fontSize(15.px).lineHeight(1.55)
          .color(DmColor.textSecondary).displayBlock().margin(bottom = 20.px),
      )
      Div(
        attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(10.px).toAttrs()
      ) {
        Button(
          "Log in to get started", variant = ButtonVariant.Primary, size = ButtonSize.Lg,
          icon = "lightning", fullWidth = true, onClick = onLogin,
        )
        Button(
          "See how it runs", variant = ButtonVariant.Ghost, iconRight = "arrow-down",
          fullWidth = true, onClick = { document.getElementById(HOW_IT_RUNS_ID)?.scrollIntoView() },
        )
      }
    }

    // Feature panels.
    Div(
      attrs = layer().displayFlex().flexDirection(FlexDirection.Column).gap(10.px)
        .padding(top = 8.px, leftRight = 16.px, bottom = 20.px).toAttrs()
    ) {
      FeaturePanel(
        "envelope-simple", DmColor.glass400, "The machine does the emailing",
        "When you're on the clock, a link lands in your inbox. Pick whenever you can.",
      )
      FeaturePanel(
        "stack", DmColor.brass400, "Any sport — even all of them",
        "NFL, MLB, NBA, golf, tennis… player pools mix sports freely.",
      )
      FeaturePanel(
        "arrows-left-right", DmColor.patina300, "Your wheel, your rules",
        "Snake, linear, or a hand-built order. The machine keeps it turning.",
      )
    }

    // How it runs.
    Div(
      attrs = layer().id(HOW_IT_RUNS_ID).padding(top = 12.px, leftRight = 20.px, bottom = 8.px)
        .toAttrs()
    ) {
      Stamp("How it runs", DmColor.textMuted, bottom = 12.px)
      Div(
        attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(12.px).toAttrs()
      ) {
        RunStep("01", "Spin up a league and bolt in the teams.")
        RunStep("02", "Set the wheel — format, rounds, order.")
        RunStep("03", "Engage. The machine runs the draft to the last pick.")
      }
    }

    // Closing CTA.
    Div(
      attrs = layer()
        .margin(top = 20.px, leftRight = 16.px, bottom = 20.px)
        .padding(18.px)
        .borderRadius(DmEffects.radiusMd)
        .border(1.px, LineStyle.Solid, DmColor.glass600)
        .backgroundColor(Color.rgba(47, 166, 168, 0.06f))
        .backgroundImageRaw(DmEffects.gradientEnergy)
        .toAttrs()
    ) {
      SpanText("No one needs to be online at once.", Modifier.roleH3().displayBlock().margin(bottom = 4.px))
      SpanText(
        "Owners are emailed automatically, picks lock in from anywhere.",
        Modifier.roleSmall().color(DmColor.textMuted).displayBlock().margin(bottom = 14.px),
      )
      Button(
        "Log in to get started", variant = ButtonVariant.Primary, icon = "lightning",
        fullWidth = true, onClick = onLogin,
      )
    }

  }
}

/** A relatively-positioned layer so content sits above the absolute cogs. */
private fun layer(): Modifier = Modifier.position(Position.Relative)

@Composable
private fun Stamp(text: String, color: Color, bottom: CSSLengthOrPercentageNumericValue? = null) {
  var mod = Modifier.dmStamp().color(color).displayBlock()
  if (bottom != null) mod = mod.margin(bottom = bottom)
  SpanText(text, mod)
}

@Composable
private fun FeaturePanel(icon: String, iconColor: Color, title: String, body: String) {
  Div(
    attrs = Modifier.dmPanel().padding(14.px).displayFlex().gap(12.px)
      .alignItems(AlignItems.FlexStart).toAttrs()
  ) {
    Icon(icon, IconWeight.Bold, Modifier.fontSize(22.px).color(iconColor).margin(top = 2.px).flex("0 0 auto"))
    Div(attrs = Modifier.toAttrs()) {
      SpanText(title, Modifier.roleH3().displayBlock().margin(bottom = 3.px))
      SpanText(body, Modifier.roleSmall().color(DmColor.textMuted).displayBlock())
    }
  }
}

@Composable
private fun RunStep(num: String, text: String) {
  Div(attrs = Modifier.displayFlex().gap(12.px).alignItems(AlignItems.Baseline).toAttrs()) {
    SpanText(
      num,
      Modifier.fontFamily(*DmType.mono).fontSize(13.px).fontWeight(DmType.weightBold)
        .color(DmColor.brass400).flex("0 0 auto"),
    )
    SpanText(text, Modifier.fontFamily(*DmType.body).fontSize(14.px).color(DmColor.textSecondary))
  }
}
